import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, RefreshCw, Pause, Play, SkipForward, Trash2, Edit, Calendar, Clock, Users, MapPin, Repeat } from 'lucide-react';

const PATTERNS = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'FORTNIGHTLY', label: 'Fortnightly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'CUSTOM', label: 'Custom' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

function CreateRecurringDialog({ open, onClose, services, teams, houses, participants, onSuccess }) {
  const [form, setForm] = useState({
    title: '',
    service_id: '',
    shift_type: 'STANDARD',
    participant_id: '',
    team_id: '',
    house_id: '',
    start_date: '',
    end_date: '',
    pattern: 'WEEKLY',
    interval: 1,
    days_of_week: [],
    occurrence_limit: '',
    start_time: '09:00',
    end_time: '17:00',
    break_duration: 0,
    required_staff: 1,
    location: '',
    notes: '',
    auto_broadcast_unfilled: true,
    assign_same_staff: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.service_id || !form.start_date || !form.start_time || !form.end_time) {
      toast.error('Please fill required fields');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        occurrence_limit: form.occurrence_limit ? parseInt(form.occurrence_limit) : null,
        end_date: form.end_date || null,
      };
      const res = await api.post('/roster/recurring', payload);
      toast.success(`Recurring series created! Generated ${res.data.generated_shifts || 0} shifts`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create recurring series');
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      days_of_week: f.days_of_week.includes(day)
        ? f.days_of_week.filter(d => d !== day)
        : [...f.days_of_week, day].sort()
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat className="w-5 h-5 text-blue-500" />
            Create Recurring Series
          </DialogTitle>
          <DialogDescription>
            Set up a recurring shift pattern that generates shifts automatically
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="title">Series Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Morning Personal Care"
                  data-testid="recurring-title"
                />
              </div>
              <div>
                <Label htmlFor="service">Service *</Label>
                <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                  <SelectTrigger data-testid="recurring-service">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="shift_type">Shift Type</Label>
                <Select value={form.shift_type} onValueChange={(v) => setForm({ ...form, shift_type: v })}>
                  <SelectTrigger data-testid="recurring-shift-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STANDARD">Standard</SelectItem>
                    <SelectItem value="SIL">SIL</SelectItem>
                    <SelectItem value="SLEEPOVER">Sleepover</SelectItem>
                    <SelectItem value="ACTIVE_NIGHT">Active Night</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Assignment */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground">Assignment (Optional)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Participant</Label>
                <Select value={form.participant_id || "none"} onValueChange={(v) => setForm({ ...form, participant_id: v === "none" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any participant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any</SelectItem>
                    {participants.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Team</Label>
                <Select value={form.team_id || "none"} onValueChange={(v) => setForm({ ...form, team_id: v === "none" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any</SelectItem>
                    {teams.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>House</Label>
                <Select value={form.house_id || "none"} onValueChange={(v) => setForm({ ...form, house_id: v === "none" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any house" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any</SelectItem>
                    {houses.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Schedule Pattern */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground">Schedule Pattern</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="recurring-start-date"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  placeholder="Leave empty for no end"
                />
              </div>
              <div>
                <Label>Pattern *</Label>
                <Select value={form.pattern} onValueChange={(v) => setForm({ ...form, pattern: v })}>
                  <SelectTrigger data-testid="recurring-pattern">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PATTERNS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Repeat Every</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    value={form.interval}
                    onChange={(e) => setForm({ ...form, interval: parseInt(e.target.value) || 1 })}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">
                    {form.pattern === 'DAILY' ? 'day(s)' : form.pattern === 'MONTHLY' ? 'month(s)' : 'week(s)'}
                  </span>
                </div>
              </div>
            </div>

            {/* Days of Week */}
            {['WEEKLY', 'FORTNIGHTLY', 'CUSTOM'].includes(form.pattern) && (
              <div>
                <Label className="mb-2 block">Days of Week</Label>
                <div className="flex gap-2 flex-wrap">
                  {DAYS_OF_WEEK.map(d => (
                    <Button
                      key={d.value}
                      type="button"
                      variant={form.days_of_week.includes(d.value) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleDay(d.value)}
                      data-testid={`recurring-day-${d.value}`}
                    >
                      {d.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Occurrence Limit</Label>
              <Input
                type="number"
                min={1}
                value={form.occurrence_limit}
                onChange={(e) => setForm({ ...form, occurrence_limit: e.target.value })}
                placeholder="Leave empty for unlimited"
                className="w-40"
              />
            </div>
          </div>

          {/* Shift Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground">Shift Details</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  data-testid="recurring-start-time"
                />
              </div>
              <div>
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  data-testid="recurring-end-time"
                />
              </div>
              <div>
                <Label>Break (min)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.break_duration}
                  onChange={(e) => setForm({ ...form, break_duration: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Required Staff</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.required_staff}
                  onChange={(e) => setForm({ ...form, required_staff: parseInt(e.target.value) || 1 })}
                  data-testid="recurring-required-staff"
                />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Optional location"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes for staff"
                rows={2}
              />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground">Options</h3>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="auto_broadcast"
                  checked={form.auto_broadcast_unfilled}
                  onCheckedChange={(v) => setForm({ ...form, auto_broadcast_unfilled: v })}
                />
                <Label htmlFor="auto_broadcast" className="cursor-pointer">Auto-broadcast unfilled shifts</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="assign_same"
                  checked={form.assign_same_staff}
                  onCheckedChange={(v) => setForm({ ...form, assign_same_staff: v })}
                />
                <Label htmlFor="assign_same" className="cursor-pointer">Assign same staff to all</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="recurring-submit">
              {loading ? 'Creating...' : 'Create Series'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SkipOccurrenceDialog({ series, open, onClose, onSuccess }) {
  const [skipDate, setSkipDate] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSkip = async () => {
    if (!skipDate) {
      toast.error('Please select a date');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/roster/recurring/${series.id}/skip?skip_date=${skipDate}`);
      toast.success(`Occurrence on ${skipDate} skipped`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to skip occurrence');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skip Occurrence</DialogTitle>
          <DialogDescription>Skip a single occurrence in the "{series?.title}" series</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Date to Skip</Label>
            <Input
              type="date"
              value={skipDate}
              onChange={(e) => setSkipDate(e.target.value)}
              data-testid="skip-date-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSkip} disabled={loading} data-testid="skip-confirm">
            {loading ? 'Skipping...' : 'Skip Occurrence'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecurringSeriesPage() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [teams, setTeams] = useState([]);
  const [houses, setHouses] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [skipDialog, setSkipDialog] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [seriesRes, servicesRes, teamsRes, housesRes, participantsRes] = await Promise.all([
        api.get('/roster/recurring'),
        api.get('/services/catalog'),
        api.get('/teams').catch(() => ({ data: { data: [] } })),
        api.get('/houses').catch(() => ({ data: { data: [] } })),
        api.get('/participants'),
      ]);
      setSeries(seriesRes.data.data || []);
      setServices(servicesRes.data.data || []);
      setTeams(teamsRes.data.data || []);
      setHouses(housesRes.data.data || []);
      setParticipants(participantsRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePause = async (s) => {
    try {
      await api.post(`/roster/recurring/${s.id}/pause`);
      toast.success('Series paused');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to pause');
    }
  };

  const handleResume = async (s) => {
    try {
      await api.post(`/roster/recurring/${s.id}/resume`);
      toast.success('Series resumed');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resume');
    }
  };

  const handleGenerate = async (s) => {
    try {
      const res = await api.post(`/roster/recurring/${s.id}/generate?days=28`);
      toast.success(`Generated ${res.data.count || 0} shifts`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate shifts');
    }
  };

  const filtered = series.filter(s => statusFilter === 'all' || s.status === statusFilter);

  const getPatternDisplay = (s) => {
    if (s.pattern === 'DAILY') return `Daily${s.interval > 1 ? ` (every ${s.interval} days)` : ''}`;
    if (s.pattern === 'WEEKLY' || s.pattern === 'CUSTOM') {
      const days = (s.days_of_week || []).map(d => DAYS_OF_WEEK.find(dw => dw.value === d)?.label).join(', ');
      return `Weekly: ${days || 'All days'}`;
    }
    if (s.pattern === 'FORTNIGHTLY') return 'Fortnightly';
    if (s.pattern === 'MONTHLY') return `Monthly${s.interval > 1 ? ` (every ${s.interval} months)` : ''}`;
    return s.pattern;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recurring-series-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Repeat className="w-6 h-6 text-blue-500" />
            Recurring Shifts
          </h1>
          <p className="text-muted-foreground mt-1">Manage recurring shift patterns that auto-generate shifts</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-recurring-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Series
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="recurring-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="PAUSED">Paused</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filtered.length} series</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Repeat className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No recurring series found</p>
            <p className="text-sm text-muted-foreground mt-1">Create a recurring series to auto-generate shifts</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filtered.map(s => (
            <Card key={s.id} data-testid={`recurring-card-${s.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg">{s.title}</h3>
                      <Badge variant={s.status === 'ACTIVE' ? 'default' : s.status === 'PAUSED' ? 'secondary' : 'outline'}>
                        {s.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{getPatternDisplay(s)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{s.start_time} - {s.end_time}</span>
                      <span className="flex items-center gap-1"><Users className="w-4 h-4" />{s.required_staff} staff</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Start: {s.start_date} | End: {s.end_date || 'Never'} | Generated: {s.shifts_generated || 0} shifts
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.status === 'ACTIVE' ? (
                      <Button size="sm" variant="outline" onClick={() => handlePause(s)} data-testid={`pause-${s.id}`}>
                        <Pause className="w-4 h-4 mr-1" />Pause
                      </Button>
                    ) : s.status === 'PAUSED' ? (
                      <Button size="sm" variant="outline" onClick={() => handleResume(s)} data-testid={`resume-${s.id}`}>
                        <Play className="w-4 h-4 mr-1" />Resume
                      </Button>
                    ) : null}
                    <Button size="sm" variant="outline" onClick={() => setSkipDialog(s)} data-testid={`skip-${s.id}`}>
                      <SkipForward className="w-4 h-4 mr-1" />Skip
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => handleGenerate(s)} data-testid={`generate-${s.id}`}>
                      <RefreshCw className="w-4 h-4 mr-1" />Generate
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateRecurringDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        services={services}
        teams={teams}
        houses={houses}
        participants={participants}
        onSuccess={fetchData}
      />

      {skipDialog && (
        <SkipOccurrenceDialog
          series={skipDialog}
          open={!!skipDialog}
          onClose={() => setSkipDialog(null)}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
}
